import type { ComponentProps } from 'react';
import { useCallback, useLayoutEffect } from 'react';
import type { Modal } from 'react-native';

import { useModalMachine } from '../../modal-machine';

/**
 * Wrapper around the Modal Machine integration.
 */
export function useModalMachineIntegration(
  props: ModalMachineIntegrationProps,
) {
  const {
    visible,
    animationType,
    onShowing,
    onShown,
    onDismissing,
    onDismissed,
  } = props;

  const modalMachineInitialState = visible ? 'shown' : 'dismissed';
  const { helpers: modalServiceHelpers } = useModalMachine({
    initial: modalMachineInitialState,
    onShowing,
    onShown,
    onDismissing,
    onDismissed,
  });

  const syncAnimationState = useCallback(() => {
    if (animationType === 'none') return;

    if (visible) {
      modalServiceHelpers.sendStartShowEvent();

      return;
    }

    modalServiceHelpers.sendStartDismissEvent();
  }, [animationType, modalServiceHelpers, visible]);

  useLayoutEffect(syncAnimationState, [syncAnimationState]);

  return { modalServiceHelpers };
}

// ─── Types ─────────────────────────────────────────────────────────────

type ModalMachineIntegrationProps = {
  readonly visible?: boolean;
  readonly animationType: ComponentProps<typeof Modal>['animationType'];
  readonly onShowing?: () => void;
  readonly onShown?: () => void;
  readonly onDismissing?: () => void;
  readonly onDismissed?: () => void;
};
